import React, { useState } from "react";
import {
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper
} from "@mui/material";

const CaloriesCalculator = () => {
  const [weight, setWeight] = useState("");
  const [weightUnit, setWeightUnit] = useState("kg");
  const [age, setAge] = useState("");
  const [neutered, setNeutered] = useState(false);
  const [condition, setCondition] = useState("normal");
  const [activity, setActivity] = useState("active");
  const [calories, setCalories] = useState(null);
  const [message, setMessage] = useState("");
  const [showMethodology, setShowMethodology] = useState(false); // Add state for toggling methodology visibility

  const handleCalculate = () => {
    setCalories(null);
    setMessage("");

    if (!weight || !age) {
      alert("Please provide weight and age.");
      return;
    }

    // If the cat is underweight, we skip the calculation and display a message.
    if (condition === "underweight") {
      setMessage("This tool is not designed for underweight cats. Please consult a veterinarian.");
      setCalories(null); // Clear previous calorie results if any
      return;
    }

    // Convert to kg if necessary
    let weightInKg = parseFloat(weight);
    if (weightUnit === "lbs") {
      weightInKg = weightInKg / 2.20462;
    }

    const ageInYears = parseInt(age);
    let rer = 70 * Math.pow(weightInKg, 0.75);

    let multiplier = 1;
    if (neutered) multiplier = 1.2;
    else multiplier = 1.4;
    if (activity === "inactive") multiplier = 1; // Lower multiplier for inactive cats
    if (condition === "overweight") multiplier = 0.8;
    if (ageInYears < 1) multiplier = 2;
    if (ageInYears < 0.3333) multiplier = 2.5;

    let finalCalories = rer * multiplier;
    setCalories(finalCalories.toFixed(0)); // Update calories if not underweight
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Estimate Daily Calories for Your Cat
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={8}>
          <TextField
            fullWidth
            label={`Weight (${weightUnit})`}
            type="number"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
          />
        </Grid>
        <Grid item xs={4}>
          <FormControl fullWidth>
            <InputLabel>Unit</InputLabel>
            <Select
              value={weightUnit}
              onChange={(e) => setWeightUnit(e.target.value)}
              label="Unit"
            >
              <MenuItem value="kg">kg</MenuItem>
              <MenuItem value="lbs">lbs</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <FormControl fullWidth sx={{ mt: 2 }}>
        <TextField
          label="Age (years)"
          type="number"
          value={age}
          onChange={(e) => setAge(e.target.value)}
        />
      </FormControl>

      <FormControlLabel
        sx={{ mt: 2 }}
        control={<Checkbox checked={neutered} onChange={(e) => setNeutered(e.target.checked)} />}
        label="Neutered/Spayed"
      />

      <FormControl fullWidth sx={{ mt: 2 }}>
        <InputLabel>Condition</InputLabel>
        <Select
          value={condition}
          onChange={(e) => setCondition(e.target.value)}
          label="Condition"
        >
          <MenuItem value="normal">Normal</MenuItem>
          <MenuItem value="overweight">Overweight</MenuItem>
          <MenuItem value="underweight">Underweight</MenuItem>
        </Select>
      </FormControl>

      <FormControl fullWidth sx={{ mt: 2 }}>
        <InputLabel>Activity Level</InputLabel>
        <Select
          value={activity}
          onChange={(e) => setActivity(e.target.value)}
          label="Activity Level"
        >
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive / Obesity-prone</MenuItem>
        </Select>
      </FormControl>

      {/* Use Grid to place buttons on the same row with space between them */}
      <Grid container spacing={2} sx={{ mt: 3 }}>
        <Grid item>
          <Button variant="contained" onClick={handleCalculate}>
            Calculate Calories
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            onClick={() => setShowMethodology(!showMethodology)}
          >
            {showMethodology ? "Hide the methodology" : "Learn more about the methodology"}
          </Button>
        </Grid>
      </Grid>

      {/* Show message for underweight cats */}
      {message && (
        <Box
          sx={{
            mt: 2,
            padding: 2,
            backgroundColor: "#ffcccc", // Light red background for underweight message
            color: "#d32f2f", // Red text color for underweight message
            borderRadius: 1,
            fontWeight: "bold",
            textAlign: "center",
            boxShadow: 3, // Adds a subtle shadow for emphasis
          }}
        >
          {message}
        </Box>
      )}

      {/* Show the result of the calculation */}
      {calories && !message && (
        <Box
          sx={{
            mt: 2,
            padding: 2,
            backgroundColor: "#e3f2fd", // Light blue background for result
            color: "#1e88e5", // Blue text color for result
            borderRadius: 1,
            fontWeight: "bold",
            textAlign: "center",
            boxShadow: 3, // Adds a subtle shadow for emphasis
          }}
        >
          Your cat's daily calorie requirement is: {calories} kcal
        </Box>
      )}



      {/* Methodology Table */}
      {showMethodology && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6">Methodology</Typography>

          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="calculation logic table">
              <TableBody>
                <TableRow>
                  <TableCell>Step 1: RER Calculation</TableCell>
                  <TableCell>
                    The Resting Energy Requirement (RER) is calculated using the formula:
                    <br />
                    <strong>70 * (Weight in kg ^ 0.75)</strong>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Step 2: Apply Multiplier</TableCell>
                  <TableCell>
                    <ul>
                      <li>Neutered cats: 1.2</li>
                      <li>Active cats: 1.4</li>
                      <li>Inactive / Obesity-prone cats: 1</li>
                      <li>Overweight cats: 0.8</li>
                      <li>Age adjustment:
                        <ul>
                          <li>If the cat is under 1 year old: 2.</li>
                          <li>If the cat is under 4 months old: 2.5.</li>
                        </ul>
                      </li>
                    </ul>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Step 3: Final Calories Calculation</TableCell>
                  <TableCell>
                    The final calorie requirement is calculated as:
                    <br />
                    <strong>RER * Multiplier</strong>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>


          {/* Sources Section */}
          <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
            Sources:
            <ul>
              <li><a href="https://www.msdvetmanual.com/management-and-nutrition/nutrition-small-animals/nutritional-requirements-of-small-animals" target="_blank" rel="noopener noreferrer">MSD Veterinary Manual</a></li>
              <li><a href="https://www.petplace.com/article/cats/pet-health/how-to-calculate-your-cats-daily-calorie-intake" target="_blank" rel="noopener noreferrer">PetPlace: How to Calculate Your Cat’s Daily Calorie Intake</a></li>
              <li><a href="https://www.petobesityprevention.org/pet-caloric-needs" target="_blank" rel="noopener noreferrer">Pet Obesity Prevention: Pet Caloric Needs</a></li>
            </ul>
          </Typography>
        </Box>
      )}

      {/* Guidelines note */}
      <Typography variant="body2" sx={{ mt: 2, textAlign: "center", fontStyle: "italic" }} color="text.secondary">
        These calorie guidelines are based on general estimates and should not replace the advice of a veterinarian. Always consult your vet for personalized recommendations.
      </Typography>
    </Box>
  );
};

export default CaloriesCalculator;
